// src/scenes/payroll/PayrollIndex.jsx
import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import DeleteIcon from "@mui/icons-material/Delete";
import StatBox from "../../components/StatBox";

const PayrollIndex = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100); // default to 100 rows per page
    const [searchQuery, setSearchQuery] = useState("");
    const [resignCode, setResignCode] = useState("All");
    const [deleteDialog, setDeleteDialog] = useState({ open: false, apiid: null });

    const formatDate = (dateStr) => {
        if (!dateStr) return "";
        const date = new Date(dateStr);
        if (isNaN(date)) return dateStr;
        const userTimezoneOffset = date.getTimezoneOffset() * 60000;
        const localDate = new Date(date.getTime() + userTimezoneOffset);
        const month = (localDate.getMonth() + 1).toString().padStart(2, "0");
        const day = localDate.getDate().toString().padStart(2, "0");
        const year = localDate.getFullYear();
        return `${month}/${day}/${year}`;
    };

    useEffect(() => {
        fetch("https://azuresql.swancreate.com/api/swa/lo_HR_PayrollCSV_Comp1")
            .then((res) => res.json())
            .then((fetchedData) => {
                setData(fetchedData);
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching payroll data:", err);
                setLoading(false);
            });
    }, []);

    const resignCodes = Array.from(
        new Set(data.map((item) => item.Resign_Code).filter(Boolean))
    );

    const filteredData = data.filter((item) => {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
            (item.FirstName && item.FirstName.toLowerCase().includes(query)) ||
            (item.LastName && item.LastName.toLowerCase().includes(query)) ||
            (item.employee_id && item.employee_id.toString().includes(query));
        const matchesResign =
            resignCode === "All" || item.Resign_Code === resignCode;
        return matchesSearch && matchesResign;
    });

    const handleCellEditCommit = React.useCallback(
        (params) => {
            const { id: apiid, field, value } = params;
            // Update local state immediately
            setData((prev) =>
                prev.map((row) => (row.apiid === apiid ? { ...row, [field]: value } : row))
            );
            // Send PUT to server using apiid as key
            fetch(
                `https://azuresql.swancreate.com/api/swa/lo_HR_PayrollCSV_Comp1/${apiid}`,
                {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ [field]: value }),
                }
            ).catch((err) =>
                console.error(`Error updating row ${apiid} field ${field}:`, err)
            );
        },
        []
    );

    const handleDeleteClick = (apiid) => () => {
        setDeleteDialog({ open: true, apiid });
    };

    const handleConfirmDelete = () => {
        const { apiid } = deleteDialog;
        fetch(
            `https://azuresql.swancreate.com/api/swa/lo_HR_PayrollCSV_Comp1/${apiid}`,
            {
                method: "DELETE",
            }
        )
            .then((res) => {
                if (!res.ok) throw new Error("Delete failed");
                setData((prev) => prev.filter((row) => row.apiid !== apiid));
                setDeleteDialog({ open: false, apiid: null });
            })
            .catch((err) => {
                console.error(`Error deleting row ${apiid}:`, err);
                setDeleteDialog({ open: false, apiid: null });
            });
    };

    const handleCancelDelete = () => {
        setDeleteDialog({ open: false, apiid: null });
    };

    const columns = [
        { field: "employee_id", headerName: "Employee ID", flex: 1, editable: true },
        { field: "LastName", headerName: "Last Name", flex: 1, editable: true },
        { field: "FirstName", headerName: "First Name", flex: 1, editable: true },
        { field: "JobID", headerName: "Job Number", flex: 1, editable: true },
        { field: "Location", headerName: "Location", flex: 1, editable: true },
        { field: "PayType", headerName: "Pay Type", flex: 1, editable: true },
        {
            field: "Resign_Code",
            headerName: "Resign Code",
            flex: 1,
            editable: true,
            type: "singleSelect",
            valueOptions: resignCodes,
        },
        {
            field: "Resign_Date",
            headerName: "Resign Date",
            flex: 1,
            editable: true,
            valueFormatter: ({ value }) => (value ? formatDate(value) : ""),
            type: "date",
        },
        {
            field: "Final_Pay_Date",
            headerName: "Final Pay Date",
            flex: 1,
            editable: true,
            valueFormatter: ({ value }) => (value ? formatDate(value) : ""),
            type: "date",
        },
        { field: "SickHours", headerName: "Sick Hours", flex: 1, editable: true },
        { field: "SickUsed", headerName: "Sick Used", flex: 1, editable: true },
        { field: "TotalSick", headerName: "Total Sick", flex: 1, editable: true },
        {
            field: "Sick_To_Be_Paid",
            headerName: "Sick To Be Paid",
            flex: 1,
            editable: true,
        },
        {
            field: "VacationHours",
            headerName: "Vacation Hours",
            flex: 1,
            editable: true,
        },
        {
            field: "VactionUsed",
            headerName: "Vacation Used",
            flex: 1,
            editable: true,
        },
        {
            field: "TotalVacation",
            headerName: "Total Vacation",
            flex: 1,
            editable: true,
        },
        {
            field: "actions",
            type: "actions",
            headerName: "Actions",
            flex: 0.5,
            getActions: (params) => [
                <GridActionsCellItem
                    icon={<DeleteIcon color="error" />}
                    label="Delete"
                    onClick={handleDeleteClick(params.id)}
                    showInMenu={false}
                />,
            ],
        },
    ];

    const downloadCSV = () => {
        const csvHeaders = columns
            .filter((col) => col.field !== "actions")
            .map((col) => col.headerName)
            .join(",");

        const csvRows = filteredData.map((row) =>
            columns
                .filter((col) => col.field !== "actions")
                .map((col) => {
                    let cell = row[col.field] ?? "";
                    if (
                        (col.field === "Resign_Date" || col.field === "Final_Pay_Date") &&
                        cell
                    ) {
                        cell = formatDate(cell);
                    }
                    if (typeof cell === "string") {
                        cell = cell.replace(/"/g, '""');
                        return `"${cell}"`;
                    }
                    return cell;
                })
                .join(",")
        );

        const csvContent = [csvHeaders, ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "payroll_data.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const totalRecords = data.length;
    const totalResignCodes = resignCodes.length;

    return (
        <Box m={2}>
            <Typography variant="h4" gutterBottom>
                Payroll Data
            </Typography>

            {/* Stat Boxes */}
            <Box display="flex" gap={2} mb={2}>
                <StatBox title={totalRecords} subtitle="Total Records" />
                <StatBox title={totalResignCodes} subtitle="Total Resign Codes" />
            </Box>

            {/* Controls */}
            <Box
                display="flex"
                flexDirection="column"
                alignItems="flex-start"
                gap={2}
                mb={2}
            >
                <Button variant="contained" color="success" onClick={downloadCSV}>
                    Download CSV
                </Button>

                <TextField
                    label="Search by First Name, Last Name, or ID"
                    variant="outlined"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    fullWidth
                />

                <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                    <InputLabel>Resign Code</InputLabel>
                    <Select
                        label="Resign Code"
                        value={resignCode}
                        onChange={(e) => setResignCode(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        {resignCodes.map((code) => (
                            <MenuItem key={code} value={code}>
                                {code}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* DataGrid with editing, delete actions, and default sort by Final_Pay_Date DESC */}
            <Paper style={{ height: "80vh", width: "100%" }}>
                <DataGrid
                    rows={filteredData}
                    columns={columns}
                    loading={loading}
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    rowsPerPageOptions={[25, 50, 100]}
                    pagination
                    getRowId={(row) => row.apiid}
                    editMode="cell"
                    onCellEditCommit={handleCellEditCommit}
                    sortModel={[{ field: "Final_Pay_Date", sort: "desc" }]}
                    sx={{
                        "& .MuiDataGrid-root": { border: "none" },
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: "#34495e",
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: "#2c3e50",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "#2c3e50",
                            borderBottom: "none",
                        },
                        "& .MuiCheckbox-root.Mui-checked": { color: "#fff" },
                    }}
                />
            </Paper>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={handleCancelDelete}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this record? This action cannot be
                        undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelDelete} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PayrollIndex;
